/**
 * Recovery helpers for stale lazy-loaded chunks.
 *
 * All routes are code-split via React.lazy. Two situations make those dynamic
 * imports reject mid-session:
 *  - Production: a redeploy replaces the content-hashed chunk files, so tabs
 *    holding the previous index.html request chunks that now 404.
 *  - Development: Vite discovers a new dependency and re-optimizes, which
 *    invalidates outstanding module URLs ("Outdated Optimize Dep"). With HMR
 *    disabled the page never receives the automatic full-reload signal.
 *
 * In both cases the correct fix is a single page reload to pick up the fresh
 * module graph. The sessionStorage guard (cleared after a short grace period)
 * ensures a genuinely broken deploy cannot trigger an infinite reload loop.
 */

const RELOAD_GUARD_KEY = "insaion:chunk-reload-at";
const RELOAD_LOOP_WINDOW_MS = 30_000;

const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "outdated optimize dep",
  "loading chunk",
  "loading css chunk",
];

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name} ${error.message}`.toLowerCase()
      : String(error).toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => message.includes(p));
}

/** Reload the page once; no-op if we already reloaded very recently. */
export function reloadOnceForStaleChunks(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - last < RELOAD_LOOP_WINDOW_MS) {
      return false; // Just reloaded — don't loop, let the error UI render.
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — still better to reload once than to hang.
  }
  window.location.reload();
  return true;
}

/**
 * Install the global Vite preload-error handler. Vite emits this event when a
 * <link rel="modulepreload"> or dynamic import of a built chunk fails.
 */
export function installStaleChunkRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForStaleChunks()) {
      // Suppress the rethrow only when we are actually reloading.
      event.preventDefault();
    }
  });
}
